import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import excelModalApi from '../../../../services/excelModalApi';

export const useFileActions = (onFileUpdate, onClose) => {
  const toast = useToast();
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectionComment, setRejectionComment] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const validateFilesForAction = (files, action) => {
    if (!files || files.length === 0) {
      return {
        valid: false,
        message: 'No files selected',
        invalidFiles: [],
      };
    }

    const invalidFiles = [];
    const validFiles = [];

    files.forEach((file) => {
      const fileStatus = file.status || 'submitted';

      if (action === 'approve') {
        if (fileStatus === 'approved') {
          invalidFiles.push({ ...file, reason: 'already approved' });
        } else if (fileStatus === 'rejected') {
          invalidFiles.push({
            ...file,
            reason: 'rejected records must be edited first',
          });
        } else {
          validFiles.push(file);
        }
      } else if (action === 'reject') {
        if (fileStatus === 'rejected') {
          invalidFiles.push({ ...file, reason: 'already rejected' });
        } else {
          // Allow both submitted and approved files to be rejected
          validFiles.push(file);
        }
      }
    });

    return {
      valid: validFiles.length > 0,
      validFiles,
      invalidFiles,
      message:
        invalidFiles.length > 0
          ? `${invalidFiles.length} record(s) cannot be ${action}d`
          : '',
    };
  };

  const approveFiles = useCallback(
    async (fileIds, filesList = []) => {
      if (!fileIds || fileIds.length === 0) {
        toast({
          title: 'No records selected',
          description: 'Please select records to approve',
          status: 'warning',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      }

      if (filesList && filesList.length > 0) {
        const validation = validateFilesForAction(filesList, 'approve');

        if (!validation.valid) {
          let message = 'Cannot approve selected record';
          if (validation.invalidFiles.length > 0) {
            const fileNames = validation.invalidFiles
              .map((f) => f.tahsil?.name || f.name || 'Unknown')
              .slice(0, 3)
              .join(', ');
            message = `${validation.invalidFiles.length} record(s) cannot be approved: ${fileNames}${validation.invalidFiles.length > 3 ? '...' : ''}`;
          }

          toast({
            title: 'Invalid selection',
            description: message,
            status: 'warning',
            duration: 5000,
            position: 'top-right',
            isClosable: true,
          });
          return null;
        }

        if (validation.invalidFiles.length > 0) {
          toast({
            title: 'Partial selection',
            description: `${validation.validFiles.length} record(s) will be approved.`,
            status: 'info',
            duration: 4000,
            position: 'top-right',
          });
        }

        fileIds = validation.validFiles.map((f) => f._id);
        if (fileIds.length === 0) return null;
      }

      setApproveLoading(true);

      try {
        const result = await excelModalApi.updateFileStatus(
          fileIds,
          'approved',
        );

        if (result.success) {
          toast({
            title: 'Success',
            description:
              result.message ||
              `${fileIds.length} record(s) approved successfully`,
            status: 'success',
            duration: 3000,
            position: 'top-right',
          });

          if (onFileUpdate) onFileUpdate();

          return result;
        } else {
          toast({
            title: 'Error',
            description: result.error,
            status: 'error',
            duration: 3000,
            position: 'top-right',
          });
          return null;
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      } finally {
        setApproveLoading(false);
      }
    },
    [toast, onFileUpdate, onClose],
  );

  const rejectFiles = useCallback(
    async (fileIds, comments, filesList = []) => {
      if (!fileIds || fileIds.length === 0) {
        toast({
          title: 'No files selected',
          description: 'Please select records to reject',
          status: 'warning',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      }

      if (!comments || comments.trim() === '') {
        toast({
          title: 'Comments required',
          description: 'Please provide rejection comments',
          status: 'warning',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      }

      if (filesList && filesList.length > 0) {
        const validation = validateFilesForAction(filesList, 'reject');

        if (!validation.valid) {
          let message = 'Cannot reject selected records';
          if (validation.invalidFiles.length > 0) {
            const fileNames = validation.invalidFiles
              .map((f) => f.tahsil?.name || f.name || 'Unknown')
              .slice(0, 3)
              .join(', ');
            message = `${validation.invalidFiles.length} Record(s) cannot be rejected: ${fileNames}${validation.invalidFiles.length > 3 ? '...' : ''}`;
          }

          toast({
            title: 'Invalid selection',
            description: message,
            status: 'warning',
            duration: 5000,
            position: 'top-right',
            isClosable: true,
          });
          return null;
        }

        if (validation.invalidFiles.length > 0 && validation.validFiles.length > 0) {
          toast({
            title: 'Partial selection',
            description: `${validation.validFiles.length} record(s) will be rejected. ${validation.invalidFiles.length} record(s) skipped.`,
            status: 'info',
            duration: 4000,
            position: 'top-right',
          });
        }

        fileIds = validation.validFiles.map((f) => f._id);
        if (fileIds.length === 0) {
          toast({
            title: 'No valid files',
            description: 'No files available for rejection',
            status: 'warning',
            duration: 3000,
            position: 'top-right',
          });
          return null;
        }
      }

      setRejectLoading(true);

      try {
        const result = await excelModalApi.updateFileStatus(
          fileIds,
          'rejected',
          comments,
        );

        if (result.success) {
          toast({
            title: 'Success',
            description:
              result.message ||
              `${fileIds.length} record(s) rejected successfully`,
            status: 'success',
            duration: 3000,
            position: 'top-right',
          });

          setRejectionComment('');
          setShowRejectInput(false);

          if (onFileUpdate) onFileUpdate();

          return result;
        } else {
          toast({
            title: 'Error',
            description: result.error,
            status: 'error',
            duration: 3000,
            position: 'top-right',
          });
          return null;
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
          position: 'top-right',
        });
        return null;
      } finally {
        setRejectLoading(false);
      }
    },
    [toast, onFileUpdate],
  );

  return {
    approveLoading,
    rejectLoading,
    rejectionComment,
    showRejectInput,
    setRejectionComment,
    setShowRejectInput,
    approveFiles,
    rejectFiles,
    validateFilesForAction,
  };
};